// API client. The access token lives only in memory; the refresh token is an
// HttpOnly cookie the browser sends to /v1/auth/refresh. A 401 triggers one
// shared refresh, then the request is retried once.

const BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "") ?? "";

// The admin console (built with --mode admin) signs in through its own
// endpoints, with its own cookie and session kind.
export const IS_ADMIN = import.meta.env.MODE === "admin";
export const AUTH = IS_ADMIN ? "/v1/admin-auth" : "/v1/auth";

let accessToken: string | null = null;
/** The current access token, for the error reporter (never stored anywhere). */
export const currentToken = () => accessToken;
let refreshing: Promise<boolean> | null = null;
const listeners = new Set<(signedIn: boolean) => void>();

export function onAuthChange(fn: (signedIn: boolean) => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

// Listeners hear only real changes. A failed session check on a signed-out
// page must not fire "signed out": that clears the query cache and strands
// queries already in flight (the invite preview, for one).
function setToken(token: string | null) {
  const changed = (accessToken === null) !== (token === null);
  accessToken = token;
  if (changed) listeners.forEach((fn) => fn(token !== null));
}

export class ApiError extends Error {
  status: number;
  code: string;
  fields?: Record<string, string>;
  constructor(status: number, code: string, message: string, fields?: Record<string, string>) {
    super(message);
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

async function parseError(res: Response): Promise<ApiError> {
  try {
    const body = await res.json();
    return new ApiError(res.status, body.error?.code ?? "error", body.error?.message ?? res.statusText, body.error?.fields);
  } catch {
    return new ApiError(res.status, "error", res.statusText || "Request failed");
  }
}

export function refreshSession(): Promise<boolean> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await fetch(`${BASE}${AUTH}/refresh`, { method: "POST", credentials: "include" });
        if (res.status === 409) {
          await new Promise((r) => setTimeout(r, 400)); // another tab is refreshing
          return refreshSession();
        }
        if (!res.ok) {
          setToken(null);
          return false;
        }
        const body = await res.json();
        setToken(body.access_token);
        return true;
      } catch {
        return false;
      } finally {
        setTimeout(() => (refreshing = null), 0);
      }
    })();
  }
  return refreshing;
}

// Settings changes may need an emailed code first (the API answers 403
// "unlock_required"). The app registers a handler that asks for the code;
// once the session is unlocked the request is sent again, once.
let unlockHandler: (() => Promise<boolean>) | null = null;
let unlocking: Promise<boolean> | null = null;
const unlockListeners = new Set<() => void>();

export function setUnlockHandler(fn: (() => Promise<boolean>) | null) {
  unlockHandler = fn;
}

/** onUnlockChange is told when settings get unlocked or locked here. */
export function onUnlockChange(fn: () => void) {
  unlockListeners.add(fn);
  return () => { unlockListeners.delete(fn); };
}
export const unlockChanged = () => unlockListeners.forEach((fn) => fn());

function askUnlock(): Promise<boolean> {
  if (!unlockHandler) return Promise.resolve(false);
  if (!unlocking) unlocking = unlockHandler().finally(() => setTimeout(() => (unlocking = null), 0));
  return unlocking;
}

export async function api<T = unknown>(path: string, init: RequestInit & { json?: unknown } = {}, retry = true, unlockRetry = true): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.json !== undefined) headers.set("Content-Type", "application/json");
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers,
    credentials: "include",
    body: init.json !== undefined ? JSON.stringify(init.json) : init.body,
  });
  if (res.status === 401 && retry && !path.startsWith(AUTH + "/")) {
    if (await refreshSession()) return api<T>(path, init, false, unlockRetry);
  }
  if (!res.ok) {
    const err = await parseError(res);
    if (err.status === 403 && err.code === "unlock_required" && unlockRetry && (await askUnlock())) return api<T>(path, init, retry, false);
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// apiBlob downloads a file (e.g. WhatsApp media) with the user's session.
export async function apiBlob(path: string, retry = true): Promise<Blob> {
  const headers = new Headers();
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  const res = await fetch(`${BASE}${path}`, { headers, credentials: "include" });
  if (res.status === 401 && retry) {
    if (await refreshSession()) return apiBlob(path, false);
  }
  if (!res.ok) throw await parseError(res);
  return res.blob();
}

// A sign-in either succeeds or asks for a code: emailed to the user, or
// (method "totp") from their authenticator app.
export interface CodeChallenge {
  challenge_token: string;
  method?: "email" | "totp";
  email_hint: string;
  remember_allowed: boolean;
  expires_at: string;
}

type SignInBody = { access_token?: string; challenge?: CodeChallenge };

function signedIn(body: SignInBody): CodeChallenge | null {
  if (body.access_token) {
    setToken(body.access_token);
    return null;
  }
  return body.challenge ?? null;
}

export async function login(email: string, password: string) {
  return signedIn(await api<SignInBody>(`${AUTH}/login`, { method: "POST", json: { email, password } }));
}

export async function verifyCode(challengeToken: string, code: string, rememberDevice: boolean) {
  const json = IS_ADMIN ? { challenge_token: challengeToken, code } : { challenge_token: challengeToken, code, remember_device: rememberDevice };
  signedIn(await api<SignInBody>(`${AUTH}/login/verify`, { method: "POST", json }));
}

// --- Passkeys (admin console "verified devices") ---

const toB64 = (buf: ArrayBuffer) => btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const fromB64 = (s: string) => Uint8Array.from(atob(s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4)), (c) => c.charCodeAt(0));

/** passkeysSupported: the browser can use passkeys with this device's own check. */
export async function passkeysSupported() {
  try {
    return typeof window.PublicKeyCredential !== "undefined" && (await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable());
  } catch { return false; }
}

/** loginWithPasskey signs in to the admin console with a verified device. */
export async function loginWithPasskey() {
  const o = await api<{ challenge_id: string; public_key: { challenge: string; rpId: string; userVerification: UserVerificationRequirement; timeout: number } }>(`${AUTH}/passkey/options`, { method: "POST", json: {} });
  const cred = await navigator.credentials.get({ publicKey: { challenge: fromB64(o.public_key.challenge), rpId: o.public_key.rpId, userVerification: "required", timeout: o.public_key.timeout } }) as PublicKeyCredential | null;
  if (!cred) throw new ApiError(0, "passkey_cancelled", "No passkey was chosen.");
  const r = cred.response as AuthenticatorAssertionResponse;
  signedIn(await api<SignInBody>(`${AUTH}/passkey/verify`, { method: "POST", json: {
    challenge_id: o.challenge_id, id: toB64(cred.rawId),
    response: { clientDataJSON: toB64(r.clientDataJSON), authenticatorData: toB64(r.authenticatorData), signature: toB64(r.signature), userHandle: r.userHandle ? toB64(r.userHandle) : "" },
  } }));
}

/** verifyThisDevice makes a passkey for the signed-in operator on this device. */
export async function verifyThisDevice(name: string) {
  type Opts = {
    challenge: string; rp: { id: string; name: string }; user: { id: string; name: string; displayName: string };
    pubKeyCredParams: PublicKeyCredentialParameters[]; authenticatorSelection: AuthenticatorSelectionCriteria; attestation: AttestationConveyancePreference;
    excludeCredentials: { type: "public-key"; id: string }[]; timeout: number;
  };
  const o = await api<{ challenge_id: string; public_key: Opts }>(`${AUTH}/passkeys/options`, { method: "POST", json: {} });
  const pk = o.public_key;
  const cred = await navigator.credentials.create({ publicKey: {
    challenge: fromB64(pk.challenge), rp: pk.rp, user: { ...pk.user, id: fromB64(pk.user.id) }, pubKeyCredParams: pk.pubKeyCredParams,
    authenticatorSelection: pk.authenticatorSelection, attestation: pk.attestation, timeout: pk.timeout,
    excludeCredentials: pk.excludeCredentials.map((c) => ({ type: c.type, id: fromB64(c.id) })),
  } }) as PublicKeyCredential | null;
  if (!cred) throw new ApiError(0, "passkey_cancelled", "The device wasn't verified.");
  const r = cred.response as AuthenticatorAttestationResponse;
  return api(`${AUTH}/passkeys`, { method: "POST", json: { challenge_id: o.challenge_id, name, response: { clientDataJSON: toB64(r.clientDataJSON), attestationObject: toB64(r.attestationObject) } } });
}

export async function resendCode(challengeToken: string, purpose: "login" | "password_reset") {
  await api(`${AUTH}/code/resend`, { method: "POST", json: { challenge_token: challengeToken, purpose } });
}

export async function acceptInvite(token: string, displayName: string, password: string) {
  return signedIn(await api<SignInBody>(`${AUTH}/invites/accept`, { method: "POST", json: { token, display_name: displayName, password } }));
}

// Sign in with Google: a real form post (a top-level navigation) so the API
// can set its state cookie and send the browser on to Google.
export function startGoogle(intent: "login" | "invite", invite = "") {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = `${BASE}/v1/auth/google/start`;
  for (const [name, value] of Object.entries({ intent, invite })) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

const completing = new Map<string, Promise<CodeChallenge | null>>();

// completeGoogle swaps the one-time ticket for a session. The page-load
// session check runs first so its "signed out" result can't land afterwards.
export function completeSupport(ticket: string) {
  let p = completing.get("s:" + ticket);
  if (!p) {
    p = (async () => {
      await refreshSession();
      return signedIn(await api<SignInBody>("/v1/auth/support/complete", { method: "POST", json: { ticket } }));
    })();
    completing.set("s:" + ticket, p);
  }
  return p;
}

export function completeGoogle(ticket: string) {
  let p = completing.get(ticket);
  if (!p) {
    p = (async () => {
      await refreshSession();
      return signedIn(await api<SignInBody>("/v1/auth/google/complete", { method: "POST", json: { ticket } }));
    })();
    completing.set(ticket, p);
  }
  return p;
}

export async function logout() {
  try {
    await api(`${AUTH}/logout`, { method: "POST" });
  } finally {
    setToken(null);
  }
}

export const apiBase = BASE;

// apiStream POSTs JSON to a Server-Sent Events endpoint and calls onEvent for
// each event. Errors before the stream starts are thrown as ApiError.
export async function apiStream(
  path: string,
  json: unknown,
  onEvent: (event: string, data: any) => void,
  signal?: AbortSignal,
  retry = true,
): Promise<void> {
  const headers = new Headers({ "Content-Type": "application/json" });
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  const res = await fetch(`${BASE}${path}`, { method: "POST", headers, credentials: "include", body: JSON.stringify(json), signal });
  if (res.status === 401 && retry && (await refreshSession())) return apiStream(path, json, onEvent, signal, false);
  if (!res.ok) throw await parseError(res);
  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() ?? "";
    for (const part of parts) {
      let event = "message";
      let data = "";
      for (const line of part.split("\n")) {
        if (line.startsWith("event: ")) event = line.slice(7);
        else if (line.startsWith("data: ")) data += line.slice(6);
      }
      try {
        onEvent(event, data ? JSON.parse(data) : {});
      } catch (err) {
        console.error(err);
      }
    }
  }
}

// apiURL is the full address of an API path (for images and links).
export const apiURL = (path: string) => `${BASE}${path}`;
